import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDexControls } from './dexControls';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function prefersReducedMotion() {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function focusableIn(node) {
  if (!node) return [];
  return [...node.querySelectorAll(FOCUSABLE)].filter((el) => {
    if (el.closest('[aria-hidden="true"]')) return false;
    return el.getClientRects().length > 0;
  });
}

function FilterDrawer({ count = 0, children }) {
  const [present, setPresent] = useState(false);
  const [open, setOpen] = useState(false);
  const [bounce, setBounce] = useState(false);
  const buttonRef = useRef(null);
  const dialogRef = useRef(null);
  const closeTimer = useRef(0);
  const bounceTimer = useRef(0);
  const openRef = useRef(false);
  openRef.current = open;

  const playBounce = useCallback(() => {
    if (prefersReducedMotion()) return;
    setBounce(false);
    window.requestAnimationFrame(() => {
      setBounce(true);
      window.clearTimeout(bounceTimer.current);
      bounceTimer.current = window.setTimeout(() => setBounce(false), 380);
    });
  }, []);

  const closeDrawer = useCallback(() => {
    setOpen(false);
    const delay = prefersReducedMotion() ? 0 : 280;
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setPresent(false), delay);
    buttonRef.current?.focus();
  }, []);

  const openDrawer = useCallback(() => {
    window.clearTimeout(closeTimer.current);
    setPresent(true);
    setOpen(true);
  }, []);

  const toggle = () => {
    playBounce();
    if (openRef.current) closeDrawer();
    else openDrawer();
  };

  useDexControls({
    onB: () => closeDrawer(),
    onA: () => {},
    onLeft: () => {},
    onRight: () => {},
    onUp: () => {},
    onDown: () => {},
  }, present);

  useEffect(() => () => {
    window.clearTimeout(closeTimer.current);
    window.clearTimeout(bounceTimer.current);
  }, []);

  useEffect(() => {
    if (!present) return undefined;
    const node = dialogRef.current;
    const first = node?.querySelector('.filter-close');
    first?.focus();
    return undefined;
  }, [present]);

  useEffect(() => {
    if (!open) return undefined;
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = 'hidden';
    return () => {
      root.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (!present) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      const combo = event.target?.closest?.('[role="combobox"]');
      if (combo?.getAttribute('aria-expanded') === 'true') return;
      event.preventDefault();
      closeDrawer();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [present, closeDrawer]);

  const onDialogKeyDown = (event) => {
    if (event.key === 'Escape') {
      const combo = event.target?.closest?.('[role="combobox"]');
      if (combo?.getAttribute('aria-expanded') === 'true') return;
      event.preventDefault();
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = focusableIn(dialogRef.current);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const badge = Number(count) || 0;
  const layer = present ? (
    <div className={open ? 'filter-layer is-open' : 'filter-layer'}>
      <button
        type="button"
        className="filter-backdrop"
        aria-label="Close filters"
        tabIndex={-1}
        onClick={closeDrawer}
      />
      <div
        ref={dialogRef}
        id="filter-drawer"
        className="filter-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="filter-drawer-title"
        data-filter-root="drawer"
        onKeyDown={onDialogKeyDown}
      >
        <div className="filter-drawer-head">
          <h2 id="filter-drawer-title">Filters</h2>
          <button type="button" className="btn btn-ghost filter-close" onClick={closeDrawer}>
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="filter-launch" data-filter-root="launch">
        <button
          ref={buttonRef}
          type="button"
          className={[
            'filter-fab',
            open ? 'is-open' : '',
            bounce ? 'is-bouncing' : '',
          ].filter(Boolean).join(' ')}
          aria-expanded={open}
          aria-controls="filter-drawer"
          aria-haspopup="dialog"
          aria-label={`Filter, ${badge} active`}
          onClick={toggle}
        >
          <span className="filter-fab-screen" aria-hidden="true">
            <svg className="icon-funnel" viewBox="0 0 24 24">
              <path d="M4 5h16l-6.2 7.2V19l-3.6 1.6v-8.4L4 5z" />
            </svg>
            <svg className="icon-x" viewBox="0 0 24 24">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </span>
          <span className="filter-fab-label">Filter</span>
          <span className="filter-badge">
            {badge}
            <span className="sr-only"> active</span>
          </span>
        </button>
      </div>
      {layer && createPortal(layer, document.body)}
    </>
  );
}

export default FilterDrawer;
